import { Link } from 'react-router-dom'
import { PageHeader, Panel, EmptyState } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money, dateShort } from '../../lib/format'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { TierBadge, type Lead } from './ClientLeads'
import { LeadsTabs } from './LeadsTabs'
import { disputeLabel } from './disputes'

const STATUS: Record<string, [string, string]> = { open: ['Being reviewed', 'is-warn'], approved: ['Refunded', 'is-good'], denied: ['Declined', ''] }
const hoursLeft = (until: string | null) => {
  if (!until) return ''
  const h = Math.max(0, Math.round((new Date(until).getTime() - Date.now()) / 3600000))
  return h >= 48 ? `${Math.floor(h / 24)} days left` : h >= 1 ? `${h} hours left` : 'under an hour left'
}

/** A client's disputes: where each one stands, and the leads they can still dispute. */
export default function ClientDisputes() {
  const { data, error } = useApi<{ leads: Lead[] }>('/api/client/leads?month=all')
  const disputed = (data?.leads || []).filter((l) => l.dispute_status)
  const eligible = (data?.leads || []).filter((l) => l.can_dispute)

  return (
    <>
      <PageHeader title="Leads" subtitle="Disputes: ask for your money back on a lead that was not what it should be." />
      <LeadsTabs />
      {error && <ErrorBlock error={error} />}
      {!data ? (!error && <LoadingBlock />) : (
        <div className="stack">
          <ol className="dispute-how">
            <li><strong>Pick the lead</strong><span className="muted">from the list below, or from the lead itself.</span></li>
            <li><strong>Say what was wrong</strong><span className="muted">one tap, a sentence, and a screenshot if you have one.</span></li>
            <li><strong>Get an answer</strong><span className="muted">if it is agreed, the money goes back to your credit or off your bill.</span></li>
          </ol>

          <Panel>
            <div className="panel-header"><h2 className="panel-title">Your disputes</h2><span className="muted">{disputed.length || ''}</span></div>
            {disputed.length === 0 ? <EmptyState title="No disputes" body="Disputes you send show up here with their answer." /> : (
              <div className="dispute-list">
                {disputed.map((l) => {
                  const [label, cls] = STATUS[l.dispute_status!]
                  return (
                    <Link key={l.id} to={`/portal/leads/${l.id}`} className="dispute-row">
                      <TierBadge name={l.tier_name} tierKey={l.tier_key} />
                      <span className="dispute-row-body">
                        <strong>{l.summary || 'Asked for a recommendation'}</strong>
                        <span className="muted">{disputeLabel(l.dispute_category)} · sent {dateShort(l.disputed_at)}</span>
                        {l.dispute_note && <span className="dispute-answer">"{l.dispute_note}"</span>}
                      </span>
                      <span className="dispute-row-end">
                        <span className={`pill ${cls}`.trim()}>{label}</span>
                        <span className="num">{money(l.list_cents ?? l.billed_cents)}</span>
                      </span>
                    </Link>
                  )
                })}
              </div>
            )}
          </Panel>

          <Panel>
            <div className="panel-header"><h2 className="panel-title">Leads you can still dispute</h2></div>
            {eligible.length === 0 ? <EmptyState title="Nothing to dispute right now" body="A lead can be disputed for a few days after it is charged." /> : (
              <div className="dispute-list">
                {eligible.map((l) => (
                  <div key={l.id} className="dispute-row">
                    <TierBadge name={l.tier_name} tierKey={l.tier_key} />
                    <span className="dispute-row-body">
                      <Link to={`/portal/leads/${l.id}`}><strong>{l.summary || 'Asked for a recommendation'}</strong></Link>
                      <span className="muted">{money(l.billed_cents)} · {hoursLeft(l.dispute_until)}</span>
                    </span>
                    <span className="dispute-row-end"><Link className="btn btn-secondary" to={`/portal/leads/${l.id}/dispute`}>Dispute</Link></span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      )}
    </>
  )
}
